/**
 * Объектная смета (Форма 3) и сводный сметный расчёт (Форма 1) в карточке сметы (СМ-05, СМ-10): таблица как в
 * печатной форме (тыс. тенге) и параметры ССР — норма главы 8 по НДЦС РК 8.04-09 (пункт таблицы 1 и зона),
 * прибыль, непредвиденные, НДС, строки частей I и III, расчётные строки главы 8, кварталы переходящего объекта.
 */
import {
  CONTINGENCY_KINDS,
  type ContingencyKind,
  type ObjectEstimateView,
  OVERHEAD_NORMS,
  SSR_ZONES,
  type SsrLine,
  type SsrParams,
  type SsrView,
  type SsrZone,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const k3 = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const LINE_GRID =
  'grid grid-cols-1 gap-2 rounded-md border border-line p-2 md:grid-cols-[1fr_1fr_120px_120px_44px]';
const QUARTER_GRID =
  'grid grid-cols-1 gap-2 rounded-md border border-line p-2 md:grid-cols-[1fr_120px_120px_44px]';
const rowClass = (kind: string) =>
  clsx(
    (kind === 'part' || kind === 'chapter') && 'bg-tile/60 font-semibold',
    kind === 'subtotal' && 'font-semibold',
    kind === 'total' && 'bg-tile font-bold',
    kind === 'note' && 'text-ink-2',
  );
const kt = (v: number | null | undefined) =>
  v === null || v === undefined || v === 0 ? '--' : k3.format(v / 1000);

/** Объектная смета — Форма 3 */
export function ObjectFormTab({ estimateId, canEdit }: { estimateId: string; canEdit: boolean }) {
  const q = useQuery({
    queryKey: ['estimates', estimateId, 'object-form'],
    queryFn: () => api<ObjectEstimateView>(`/estimates/${estimateId}/object-form`),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  return (
    <div className="flex flex-col gap-6">
      {canEdit ? <ObjectSigners estimateId={estimateId} /> : null}
      <Panel
        title="Объектная смета — Форма 3"
        meta="НДЦС РК 8.01-08-2022, приложение Г · тыс. тенге"
        bodyClassName="p-0"
      >
        {v.warnings.length ? (
          <ul className="border-b border-line bg-warning-soft/40 px-5 py-2 text-[13px] text-ink">
            {v.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        ) : null}
        {v.rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-[13px]">
              <thead className="bg-tile text-left text-ink-2">
                <tr>
                  <th className="px-3 py-2">№</th>
                  <th className="px-3 py-2">Номер сметы</th>
                  <th className="px-3 py-2">Наименование работ и затрат</th>
                  <th className="num px-3 py-2 text-right">СМР</th>
                  <th className="num px-3 py-2 text-right">Оборудование</th>
                  <th className="num px-3 py-2 text-right">Прочие</th>
                  <th className="num px-3 py-2 text-right">Всего</th>
                  <th className="num px-3 py-2 text-right">Трудоёмкость, тыс. чел.-ч</th>
                  <th className="num px-3 py-2 text-right">Оплата труда</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {v.rows.map((r) => (
                  <tr key={r.no}>
                    <td className="px-3 py-2">{r.no}</td>
                    <td className="px-3 py-2">
                      {r.estimateId ? (
                        <Link
                          to="/estimates/$estimateId"
                          params={{ estimateId: r.estimateId }}
                          className="inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-0"
                        >
                          {r.code}
                        </Link>
                      ) : (
                        r.code
                      )}
                    </td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="num px-3 py-2 text-right">{kt(r.smr)}</td>
                    <td className="num px-3 py-2 text-right">{kt(r.equipment)}</td>
                    <td className="num px-3 py-2 text-right">{kt(r.other)}</td>
                    <td className="num px-3 py-2 text-right font-medium">{kt(r.total)}</td>
                    <td className="num px-3 py-2 text-right">
                      {r.laborHours ? k3.format(r.laborHours / 1000) : '--'}
                    </td>
                    <td className="num px-3 py-2 text-right">{kt(r.wages)}</td>
                  </tr>
                ))}
                <tr className="bg-tile font-semibold">
                  <td className="px-3 py-2" colSpan={3}>
                    Всего
                  </td>
                  <td className="num px-3 py-2 text-right">{kt(v.totals.smr)}</td>
                  <td className="num px-3 py-2 text-right">{kt(v.totals.equipment)}</td>
                  <td className="num px-3 py-2 text-right">{kt(v.totals.other)}</td>
                  <td className="num px-3 py-2 text-right">{kt(v.totals.total)}</td>
                  <td className="num px-3 py-2 text-right">
                    {v.totals.laborHours ? k3.format(v.totals.laborHours / 1000) : '--'}
                  </td>
                  <td className="num px-3 py-2 text-right">{kt(v.totals.wages)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-5 py-4">
            <EmptyState title="Локальные сметы не привязаны">
              В карточке локальной сметы укажите эту объектную смету как родительскую.
            </EmptyState>
          </div>
        )}
      </Panel>
    </div>
  );
}

/** Подписи объектной сметы (Форма 3): ГИП, начальник сметного отдела, составил, проверил */
function ObjectSigners({ estimateId }: { estimateId: string }) {
  const qc = useQueryClient();
  const card = useQuery({
    queryKey: ['estimates', estimateId, 'object-signers'],
    queryFn: () => api<{ params: SsrParams }>(`/estimates/${estimateId}/object-signers`),
  });
  const [signers, setSigners] = useState<NonNullable<SsrParams['signers']>>({});
  useEffect(() => {
    if (card.data) setSigners(card.data.params.signers ?? {});
  }, [card.data]);
  const save = useMutation({
    mutationFn: () =>
      api(`/estimates/${estimateId}/ssr`, { method: 'PUT', body: { ...(card.data?.params ?? {}), signers } }),
    onSuccess: () => {
      toast('Подписи сохранены');
      qc.invalidateQueries({ queryKey: ['estimates', estimateId] });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const f = (k: keyof NonNullable<SsrParams['signers']>, label: string) => (
    <Field label={label} htmlFor={`os-${k}`}>
      <Input
        id={`os-${k}`}
        value={signers[k] ?? ''}
        onChange={(e) => setSigners({ ...signers, [k]: e.target.value })}
        className="min-h-[44px] md:min-h-0"
      />
    </Field>
  );
  return (
    <Panel title="Подписи" meta="для печатной формы">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        {f('gip', 'Главный инженер проекта')}
        {f('head', 'Начальник сметного отдела')}
        {f('estimator', 'Составил')}
        {f('checker', 'Проверил')}
      </div>
      <div className="mt-3 flex justify-end">
        <Button className="min-h-[44px] md:min-h-0" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить подписи
        </Button>
      </div>
    </Panel>
  );
}

function NumInput({
  value,
  onChange,
  step = 'any',
  className,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  step?: string;
  className?: string;
}) {
  return (
    <Input
      type="number"
      inputMode="decimal"
      step={step}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      className={clsx('min-h-[44px] md:min-h-0', className)}
    />
  );
}

function LinesEditor({
  title,
  hint,
  lines,
  onChange,
  withSmr,
  withPct,
}: {
  title: string;
  hint: string;
  lines: SsrLine[];
  onChange: (l: SsrLine[]) => void;
  withSmr?: boolean;
  withPct?: boolean;
}) {
  const set = (i: number, patch: Partial<SsrLine>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-ink">{title}</span>
        <Button
          size="sm"
          className="min-h-[44px] md:min-h-0"
          icon={<Plus className="size-4" />}
          onClick={() => onChange([...lines, { name: '', amount: 0 }])}
        >
          Строка
        </Button>
      </div>
      <p className="text-[12px] text-ink-2">{hint}</p>
      {lines.map((l, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: строки без своего идентификатора, порядок и есть ключ
        <div key={i} className={LINE_GRID}>
          <Input
            value={l.name}
            onChange={(e) => set(i, { name: e.target.value })}
            placeholder="Наименование"
            className="min-h-[44px] md:min-h-0"
          />
          <Input
            value={l.basis ?? ''}
            onChange={(e) => set(i, { basis: e.target.value })}
            placeholder="Обоснование"
            className="min-h-[44px] md:min-h-0"
          />
          {withPct ? (
            <NumInput value={l.pct ?? null} onChange={(v) => set(i, { pct: v ?? undefined })} />
          ) : withSmr ? (
            <NumInput value={l.amountSmr ?? null} onChange={(v) => set(i, { amountSmr: v ?? undefined })} />
          ) : (
            <NumInput value={l.year ?? null} onChange={(v) => set(i, { year: v ?? undefined })} step="1" />
          )}
          <NumInput value={l.amount} onChange={(v) => set(i, { amount: v ?? 0 })} />
          <Button
            size="sm"
            variant="ghost"
            className="min-h-[44px] md:min-h-0"
            aria-label="Убрать строку"
            icon={<Trash2 className="size-4" />}
            onClick={() => onChange(lines.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      {lines.length ? (
        <p className="text-[12px] text-ink-3">
          Колонки: наименование, обоснование,{' '}
          {withPct ? '% от итога части II' : withSmr ? 'сумма в графу СМР, тенге' : 'год'}, сумма в графу
          «прочие», тенге
        </p>
      ) : null}
    </div>
  );
}

/** Сводный сметный расчёт — Форма 1 с параметрами */
export function SsrTab({ estimateId, canEdit }: { estimateId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['estimates', estimateId, 'ssr'],
    queryFn: () => api<SsrView>(`/estimates/${estimateId}/ssr`),
  });
  const [p, setP] = useState<SsrParams | null>(null);
  // Сервер обновился (сохранил другой человек или сами) — берём его параметры
  useEffect(() => {
    if (q.data) setP(q.data.params);
  }, [q.data]);
  const save = useMutation({
    mutationFn: (body: SsrParams) => api<SsrView>(`/estimates/${estimateId}/ssr`, { method: 'PUT', body }),
    onSuccess: (v) => {
      qc.setQueryData(['estimates', estimateId, 'ssr'], v);
      setP(v.params);
      toast('Параметры сводного расчёта сохранены');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  const params = p ?? v.params;
  const upd = (patch: Partial<SsrParams>) => setP({ ...params, ...patch });
  return (
    <div className="flex flex-col gap-6">
      {canEdit ? (
        <Panel title="Параметры сводного расчёта" meta="НДЦС РК 8.01-08-2022, 8.04-09-2022">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label="Заказчик" htmlFor="ssr-customer">
              <Input
                id="ssr-customer"
                value={params.customer ?? ''}
                onChange={(e) => upd({ customer: e.target.value || null })}
                className="min-h-[44px] md:min-h-0"
              />
            </Field>
            <Field label="Документ об утверждении" htmlFor="ssr-approval">
              <Input
                id="ssr-approval"
                value={params.approvalRef ?? ''}
                onChange={(e) => upd({ approvalRef: e.target.value || null })}
                className="min-h-[44px] md:min-h-0"
              />
            </Field>
            <Field
              label="Стадия"
              htmlFor="ssr-stage"
              hint="От стадии зависит норма непредвиденных (п. 8.2.66)"
            >
              <Select
                id="ssr-stage"
                value={params.stage}
                onChange={(e) => upd({ stage: e.target.value as 'p' | 'rp' })}
                className="min-h-[44px] md:min-h-0"
              >
                <option value="rp">Рабочий проект</option>
                <option value="p">ТЭО / Проект</option>
              </Select>
            </Field>
            <Field label="Вид строительства (таблица 1 НДЦС РК 8.04-09)" htmlFor="ssr-overhead">
              <Select
                id="ssr-overhead"
                value={params.overheadCode ?? ''}
                onChange={(e) => upd({ overheadCode: e.target.value || null })}
                className="min-h-[44px] md:min-h-0"
              >
                <option value="">— не выбран —</option>
                {OVERHEAD_NORMS.map((n) => (
                  <option key={n.code} value={n.code}>
                    {n.code} {n.name} ({n.zones.join(' / ')} %)
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Температурная зона (приложение Б)" htmlFor="ssr-zone">
              <Select
                id="ssr-zone"
                value={params.zone}
                onChange={(e) => upd({ zone: e.target.value as SsrZone })}
                className="min-h-[44px] md:min-h-0"
              >
                {(Object.keys(SSR_ZONES) as SsrZone[]).map((z) => (
                  <option key={z} value={z}>
                    {SSR_ZONES[z]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Норма главы 8 вручную, %"
              htmlFor="ssr-overhead-pct"
              hint="Если вид строительства не из списка; пусто — по таблице"
            >
              <NumInput value={params.overheadPct ?? null} onChange={(x) => upd({ overheadPct: x })} />
            </Field>
            <Field label="Сметная прибыль, % от СМР глав 1–8" htmlFor="ssr-profit" hint="п. 8.2.65.2 — 5 %">
              <NumInput value={params.profitPct} onChange={(x) => upd({ profitPct: x ?? 5 })} />
            </Field>
            <Field label="Непредвиденные: назначение объекта" htmlFor="ssr-cont">
              <Select
                id="ssr-cont"
                value={params.contingencyKind}
                onChange={(e) => upd({ contingencyKind: e.target.value as ContingencyKind })}
                className="min-h-[44px] md:min-h-0"
              >
                {(Object.keys(CONTINGENCY_KINDS) as ContingencyKind[]).map((k) => (
                  <option key={k} value={k}>
                    {CONTINGENCY_KINDS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Непредвиденные вручную, %"
              htmlFor="ssr-cont-pct"
              hint="Пусто — по стадии и назначению (п. 8.2.66)"
            >
              <NumInput value={params.contingencyPct ?? null} onChange={(x) => upd({ contingencyPct: x })} />
            </Field>
            <Field
              label="НДС, %"
              htmlFor="ssr-vat"
              hint="Пусто — по дате уровня цен: 12 % до 2026 года, 16 % с 2026 года"
            >
              <NumInput value={params.vatPct ?? null} onChange={(x) => upd({ vatPct: x })} />
            </Field>
            <Field label="Подписи: руководитель / ГИП / сметный отдел" htmlFor="ssr-signers">
              <div className="flex flex-col gap-1">
                <Input
                  id="ssr-signers"
                  placeholder="Руководитель проектной организации"
                  value={params.signers?.head ?? ''}
                  onChange={(e) => upd({ signers: { ...params.signers, head: e.target.value } })}
                  className="min-h-[44px] md:min-h-0"
                />
                <Input
                  placeholder="Главный инженер проекта"
                  value={params.signers?.gip ?? ''}
                  onChange={(e) => upd({ signers: { ...params.signers, gip: e.target.value } })}
                  className="min-h-[44px] md:min-h-0"
                />
                <Input
                  placeholder="Начальник сметного отдела"
                  value={params.signers?.estimator ?? ''}
                  onChange={(e) => upd({ signers: { ...params.signers, estimator: e.target.value } })}
                  className="min-h-[44px] md:min-h-0"
                />
              </div>
            </Field>
          </div>
          <div className="mt-4 flex flex-col gap-5">
            <LinesEditor
              title="Часть I. Проектирование"
              hint="Изыскания, проектные работы, экспертиза — суммы в графу «прочие» (п. 8.2.58)"
              lines={params.partI}
              onChange={(l) => upd({ partI: l })}
            />
            <LinesEditor
              title="Глава 8. Расчётные строки по ПОС"
              hint="Временное отопление, подъездные дороги, мобилизация техники, перевозка работников, вахта (раздел 5 НДЦС РК 8.04-09); общеплощадочные по норме добавляются сами"
              lines={params.chapter8}
              onChange={(l) => upd({ chapter8: l })}
              withSmr
            />
            <LinesEditor
              title="Часть III. Инжиниринговые услуги"
              hint="Авторский и технический надзор, управление проектом — процентом от итога части II или суммой (приложение Б)"
              lines={params.partIII}
              onChange={(l) => upd({ partIII: l })}
              withPct
            />
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-medium text-ink">
                  Переходящий объект: кварталы и индексы (НДЦС РК 8.04-07)
                </span>
                <Button
                  size="sm"
                  className="min-h-[44px] md:min-h-0"
                  icon={<Plus className="size-4" />}
                  onClick={() => upd({ quarters: [...params.quarters, { label: '', share: 0, index: 1 }] })}
                >
                  Квартал
                </Button>
              </div>
              {params.quarters.map((qq, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: кварталы без своего идентификатора
                <div key={i} className={QUARTER_GRID}>
                  <Input
                    value={qq.label}
                    placeholder="I квартал 2027 г."
                    onChange={(e) =>
                      upd({
                        quarters: params.quarters.map((x, j) =>
                          j === i ? { ...x, label: e.target.value } : x,
                        ),
                      })
                    }
                    className="min-h-[44px] md:min-h-0"
                  />
                  <NumInput
                    value={qq.share}
                    onChange={(x) =>
                      upd({
                        quarters: params.quarters.map((y, j) => (j === i ? { ...y, share: x ?? 0 } : y)),
                      })
                    }
                  />
                  <NumInput
                    value={qq.index}
                    onChange={(x) =>
                      upd({
                        quarters: params.quarters.map((y, j) => (j === i ? { ...y, index: x ?? 1 } : y)),
                      })
                    }
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-[44px] md:min-h-0"
                    aria-label="Убрать квартал"
                    icon={<Trash2 className="size-4" />}
                    onClick={() => upd({ quarters: params.quarters.filter((_, j) => j !== i) })}
                  />
                </div>
              ))}
              {params.quarters.length ? (
                <p className="text-[12px] text-ink-3">Колонки: квартал, доля %, индекс</p>
              ) : null}
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              className="min-h-[44px] md:min-h-0"
              variant="primary"
              loading={save.isPending}
              onClick={() => save.mutate(params)}
            >
              Пересчитать и сохранить
            </Button>
          </div>
        </Panel>
      ) : null}

      <Panel
        title="Сводный сметный расчёт — Форма 1"
        meta={`глава 8 — ${v.overheadPct} %, прибыль ${v.params.profitPct} %, непредвиденные ${v.contingencyPct} %, НДС ${v.vatPct} % · тыс. тенге`}
        bodyClassName="p-0"
      >
        {v.warnings.length ? (
          <ul className="border-b border-line bg-warning-soft/40 px-5 py-2 text-[13px] text-ink">
            {v.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-[13px]">
            <thead className="bg-tile text-left text-ink-2">
              <tr>
                <th className="px-3 py-2">№</th>
                <th className="px-3 py-2">Обоснование</th>
                <th className="px-3 py-2">Наименование частей, глав, объектов, работ и затрат</th>
                <th className="num px-3 py-2 text-right">СМР</th>
                <th className="num px-3 py-2 text-right">Оборудование</th>
                <th className="num px-3 py-2 text-right">Прочие</th>
                <th className="num px-3 py-2 text-right">Всего</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {v.rows.map((r, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: строки расчёта только читаются, порядок фиксирован
                <tr key={i} className={rowClass(r.kind)}>
                  <td className="px-3 py-2">{r.no}</td>
                  <td className="px-3 py-2 text-ink-2">{r.basis}</td>
                  <td className="px-3 py-2" colSpan={r.kind === 'part' || r.kind === 'chapter' ? 5 : 1}>
                    {r.name}
                  </td>
                  {r.kind === 'part' || r.kind === 'chapter' ? null : (
                    <>
                      <td className="num px-3 py-2 text-right">{kt(r.smr)}</td>
                      <td className="num px-3 py-2 text-right">{kt(r.equipment)}</td>
                      <td className="num px-3 py-2 text-right">{kt(r.other)}</td>
                      <td className="num px-3 py-2 text-right">{kt(r.total)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
